import { useContext, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { WorkbenchContext } from './WorkbenchContext';

/*
 * A drawer of the analysis workbench (docs/ANALYSIS_WORKBENCH.md section 5):
 * a panel docked on one side of the stage, over the canvas, never resizing
 * it.
 *
 *   <WorkbenchDrawer id="threshold" side="end" title={...} icon="🎚️" order={20}>
 *
 * `label` is its toggle's name when the title is long (default: the title).
 * It registers itself with the shell, whose toolbar then shows its toggle,
 * and is placed (a portal) in its side's dock. Always mounted: closed, it is
 * `hidden`, its content kept. Non-modal, like the Field camera's controls
 * sheet: the dialog role, not modal, named by its title; the canvas and
 * the toolbar stay usable, Tab may leave it. Opened by a person, focus goes to
 * its title; closed, focus goes back to what opened it (the shell keeps that),
 * also when Back closed it while focus was inside.
 *
 * `forceOpen` shows it whatever the address says (the culture mode's source
 * drawer while nothing is on the stage); its x then calls `onDismiss`.
 * Outside a shell it renders in place, open.
 */
export const SIDES = Object.freeze(['start', 'end', 'bottom']);

export default function WorkbenchDrawer({
  id, side = 'end', title, label = null, icon = null, order = 50, forceOpen = false, onDismiss = null, testId, children,
}) {
  const wb = useContext(WorkbenchContext);
  const { t } = useTranslation('workbench');
  const titleId = useId();
  const titleRef = useRef(null);
  const panelRef = useRef(null);
  const hadFocus = useRef(false);
  const where = SIDES.includes(side) ? side : 'end';
  const register = wb?.registerDrawer;
  const titleText = typeof title === 'string' ? title : '';
  // Its toggle's name: `label` when the title is long ("1 · Choose the dish photograph" -> "Dish photograph").
  const toggleText = typeof label === 'string' && label ? label : titleText;

  // The shell's list of drawers (its toggles), kept in the commit that mounts this one.
  useLayoutEffect(() => (register ? register({ id, side: where, title: titleText, label: toggleText, icon, order, forceOpen })
    : undefined), [register, id, where, titleText, toggleText, icon, order, forceOpen]);
  useLayoutEffect(() => { wb?.setDismiss?.(id, onDismiss); });

  const open = wb ? wb.drawers.isOpen(id) : true;

  // Opened by a person: focus to the title, so a screen reader says where they are, or to the
  // element the opener named (drawers.open(id, {focus: () => element})), now that it is shown.
  useEffect(() => {
    const request = open ? wb?.takeFocusRequest?.(id) : null;
    if (request) (request.target?.() || titleRef.current)?.focus?.({ preventScroll: true });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closed while focus was inside (Back, a link): focus goes back to what opened it.
  useEffect(() => {
    if (open || !hadFocus.current) return;
    hadFocus.current = false;
    wb?.returnFocus?.(id);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const panel = (
    <section ref={panelRef} id={`wb-drawer-${id}`} className="wb-drawer" role="dialog" aria-modal="false"
             aria-labelledby={titleId} data-drawer={id} data-side={where} data-testid={testId}
             hidden={!open} style={{ display: open ? 'flex' : 'none', order }}
             onFocus={() => { hadFocus.current = true; }}
             onBlur={(e) => { if (!panelRef.current?.contains(e.relatedTarget)) hadFocus.current = false; }}>
      <header className="wb-drawer-head">
        {icon && <span aria-hidden="true" className="wb-icon">{icon}</span>}
        <h2 ref={titleRef} id={titleId} tabIndex={-1} className="wb-drawer-title">{title}</h2>
        {wb && (
          <button type="button" className="wb-btn wb-close" aria-label={t('drawer.close', { title: titleText })}
                  title={t('drawer.close', { title: titleText })} data-testid={`wb-close-${id}`}
                  onClick={() => wb.drawers.close(id)}>
            <span aria-hidden="true">×</span>
          </button>
        )}
      </header>
      <div className="wb-drawer-body" data-testid={`wb-body-${id}`}>{children}</div>
    </section>
  );
  if (!wb) return panel;
  const dock = wb.dock(where);
  return dock ? createPortal(panel, dock) : null;
}
